import React, { useState, useEffect, useMemo } from "react";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import handleService from "../../services/apiService.ts";
import { MethodsEnum } from "../../enums/Methods.ts";
import { formatCNPJ } from "../../utils/cnpj.util.ts";
import UpdateConvenioDrawer from "../../components/Modals/UpdateConvenioDrawer/index.tsx";
import CreateConvenioDrawer from "../../components/Modals/CreateConvenioDrawer/index.tsx";
import ConfirmDeleteDialog from "../../components/Modals/ConfirmDeleteDialog/index.tsx";
import { Response } from "../../types/Api.ts";
import Button from "../../ui/Button.tsx";
import SearchField from "../../ui/SearchField.tsx";
import EmptyState from "../../ui/EmptyState.tsx";
import SkeletonRows from "../../ui/SkeletonRows.tsx";
import "../../styles/design-system.css";

interface Convenio {
  id: string;
  nome: string;
  documento: string;
}

const ListagemConvenio = () => {
  const [convenios, setConvenios] = useState<Convenio[]>([]);
  const [busca, setBusca] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [currentConvenio, setCurrentConvenio] = useState<Convenio | null>(null);
  const [convenioParaExcluir, setConvenioParaExcluir] = useState<Convenio | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExcluindo, setIsExcluindo] = useState(false);

  useEffect(() => {
    fetchConvenios();
  }, []);

  const fetchConvenios = async () => {
    try {
      setIsLoading(true);
      const response = await handleService({ endpoint: "/convenio" });
      const lista: Convenio[] = Array.isArray(response) ? response : response.data || [];
      setConvenios(lista);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditar = (convenio: Convenio) => {
    setCurrentConvenio(convenio);
    setIsEditOpen(true);
  };

  const handleVisualizar = (convenio: Convenio) => {
    setCurrentConvenio(convenio);
    setIsViewOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditOpen(false);
    setCurrentConvenio(null);
  };

  const handleCloseView = () => {
    setIsViewOpen(false);
    setCurrentConvenio(null);
  };

  // POST/PATCH de convênio respondem com CadastroConvenioResponse, que usa o
  // campo "cnpj" — já a listagem (GET /convenio) usa DadosListarConvenio, que
  // chama o mesmo dado de "documento". Sem esse mapeamento, o valor cru da
  // resposta de criar/editar não tem "documento", e a coluna CNPJ fica em
  // branco na tabela até a próxima recarga (o dado em si é salvo certo).
  const onUpdate = (response: Response<Convenio & { cnpj?: string }>) => {
    const convenio = response.data;
    if (convenio) {
      const convenioCorrigido: Convenio = { ...convenio, documento: convenio.documento ?? convenio.cnpj ?? "" };
      setConvenios((prev) => prev.map((c) => (c.id === convenioCorrigido.id ? convenioCorrigido : c)));
      setIsEditOpen(false);
    }
  };

  const onCreated = (response: Response<Convenio & { cnpj?: string }>) => {
    const convenio = response.data;
    if (convenio) {
      const convenioCorrigido: Convenio = { ...convenio, documento: convenio.documento ?? convenio.cnpj ?? "" };
      setConvenios((prev) => [...prev, convenioCorrigido]);
      setIsCreateOpen(false);
    }
  };

  const handleConfirmarExclusao = async () => {
    if (!convenioParaExcluir) return;
    try {
      setIsExcluindo(true);
      await handleService({ endpoint: `/convenio/${convenioParaExcluir.id}`, method: MethodsEnum.DELETE });
      setConvenios((prev) => prev.filter((c) => c.id !== convenioParaExcluir.id));
      setConvenioParaExcluir(null);
    } catch (error) {
    } finally {
      setIsExcluindo(false);
    }
  };

  const filtrados = useMemo(
    () => convenios.filter((c) => c.nome?.toLowerCase().includes(busca.toLowerCase())),
    [convenios, busca]
  );

  return (
    <div className="ds">
      <div className="ds-page-head">
        <div>
          <h1>Convênios</h1>
          <p>Visualize e gerencie os convênios da sua clínica.</p>
        </div>
        <div className="ds-page-head-actions">
          <Button variant="primary" icon={<AddCircleOutlineIcon fontSize="small" />} onClick={() => setIsCreateOpen(true)}>
            Novo convênio
          </Button>
        </div>
      </div>

      <div className="ds-toolbar">
        <SearchField value={busca} onChange={setBusca} placeholder="Buscar por nome..." />
      </div>

      <div className="ds-table-wrap">
        <table className="ds-table">
          <thead>
            <tr>
              <th>Nome do convênio</th>
              <th>CNPJ</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows columns={3} />
            ) : filtrados.length === 0 ? (
              <tr>
                <td colSpan={3}>
                  <EmptyState title="Nenhum convênio encontrado" description="Cadastre o primeiro convênio para começar." actionLabel="Novo convênio" onAction={() => setIsCreateOpen(true)} />
                </td>
              </tr>
            ) : (
              filtrados.map((convenio) => (
                <tr key={convenio.id}>
                  <td className="ds-cell-title">{convenio.nome}</td>
                  <td>{formatCNPJ(convenio.documento)}</td>
                  <td>
                    <div className="ds-row-actions">
                      <button className="ds-icon-btn" onClick={() => handleVisualizar(convenio)} title="Visualizar">
                        <VisibilityOutlinedIcon fontSize="small" />
                      </button>
                      <button className="ds-icon-btn" onClick={() => handleEditar(convenio)} title="Editar">
                        <EditOutlinedIcon fontSize="small" />
                      </button>
                      <button className="ds-icon-btn" onClick={() => setConvenioParaExcluir(convenio)} title="Excluir">
                        <DeleteOutlineIcon fontSize="small" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <UpdateConvenioDrawer isOpen={isEditOpen} onClose={handleCloseEdit} convenio={currentConvenio} onUpdate={onUpdate} />
      <UpdateConvenioDrawer isOpen={isViewOpen} onClose={handleCloseView} convenio={currentConvenio} readOnly />
      <CreateConvenioDrawer open={isCreateOpen} onClose={() => setIsCreateOpen(false)} onCreated={onCreated} />

      <ConfirmDeleteDialog
        open={!!convenioParaExcluir}
        title="Excluir Convênio"
        message="Tem certeza que deseja excluir este convênio cadastrado? Esta ação não poderá ser desfeita."
        loading={isExcluindo}
        onCancel={() => setConvenioParaExcluir(null)}
        onConfirm={handleConfirmarExclusao}
      />
    </div>
  );
};

export default ListagemConvenio;
